/**
 * Payment Error Utilities — Shared across checkout and dashboard
 *
 * Contains the canonical error catalog (ERROR_MESSAGES), error parser
 * (parsePaymentError), and shared error handler (handlePaymentError).
 *
 * Error codes (CS-XXX) are for backend logging and Sentry only — never shown to users.
 * See docs/initiatives/completed/payment-error-codes/error-messages-final.md for the
 * canonical error catalog.
 */

// ============================================
// ERROR MESSAGES — Canonical catalog
// ============================================
export const ERROR_MESSAGES = {
  // STRIPE CARD ERRORS (CS-SCxx)
  'CS-SC01': "This card couldn't be processed. Please try another payment method.",
  'CS-SC02': "Security code doesn't match. Please check the 3-digit code on the back of your card.",
  'CS-SC03': "Entered zip code does not match your card.",
  'CS-SC04': "This card has expired. Please use a different card or payment method.",
  'CS-SC05': "This card cannot be used. Please use another method or contact your bank if you believe this is an error.",
  'CS-SC06': "Insufficient funds, please use a different payment method.",
  'CS-SC07': "Invalid card number. Please check your card number and try again.",
  'CS-SC08': "Your bank declined this transaction. Please try another payment method.",
  'CS-SC09': "This transaction flagged for security. Please contact your bank or try a different payment method.",

  // FINIX CARD ERRORS (CS-FCxx)
  'CS-FC01': "This card couldn't be processed. Please try another payment method.",
  'CS-FC02': "Security code doesn't match. Please check the 3-digit code on the back of your card.",
  'CS-FC03': "Entered zip code does not match your card.",
  'CS-FC04': "This card has expired. Please use a different card or payment method.",
  'CS-FC05': "This card cannot be used. Please use another method or contact your bank if you believe this is an error.",
  'CS-FC06': "Insufficient funds, please use a different payment method.",
  'CS-FC07': "Invalid card number. Please check your card number and try again.",
  'CS-FC08': "Your bank declined this transaction. Please try another payment method.",
  'CS-FC09': "This transaction flagged for security. Please contact your bank or try a different payment method.",
  'CS-FC10': "This appears to be a duplicate transaction. If you intended to donate again, please wait a moment and try again.",
  'CS-FC11': "Your card does not allow recurring transactions. Please contact your bank or use a different card.",
  'CS-FC12': "The billing address doesn't match your card. Please verify your address and try again.",
  'CS-FC13': "A network error occurred. Please try again.",
  'CS-FC14': "Your bank is temporarily unavailable. Please try again in a few minutes.",
  'CS-FC15': "Payment request timed out. Please check your connection and try again.",
  'CS-FC16': "Payment could not be processed. Please try again or use a different payment method.",
  'CS-FC17': "Payment form is still loading. Please wait a moment and try again.",
  'CS-FC18': "Payment could not be submitted. Please try again.",

  // STRIPE BANK ERRORS (CS-SBxx)
  'CS-SB01': "Bank verification failed. Try again or use another payment method.",
  'CS-SB02': "Bank account insufficient funds. Try another payment method.",
  'CS-SB03': "Bank account invalid for payments. Try another payment method.",

  // FINIX BANK ERRORS (CS-FBxx)
  'CS-FB01': "Bank verification failed. Try again or use another payment method.",
  'CS-FB02': "Bank account insufficient funds. Try another payment method.",
  'CS-FB03': "Bank account invalid for payments. Try another payment method.",

  // INTERNAL SYSTEM ERRORS (CS-Ixx)
  'CS-I01': "Card payment could not be completed. Please try again.",
  'CS-I02': "Card payment could not be completed. Please try again.",
  'CS-I03': "Card payment could not be completed. Please try again.",
  'CS-I04': "Card payment could not be completed. Please try again.",
  'CS-I05': "Payment server returned invalid response. Please try again.",
  'CS-I06': "Payment data validation failed. Please try again.",

  // VALIDATION ERRORS (CS-Vxx) — Request-level validation failures from backend
  'CS-V01': "Checkout session expired. Please return to the donation form and try again.",

  // SECURITY ERRORS (CS-Xxx)
  'CS-X02': "Unable to process this transaction. Please contact support.",

  // EXPRESS CHECKOUT ERRORS (CS-Exx)
  'CS-E01': "This payment method is down. Please refresh or use a different payment method.",
  'CS-E02': "Payment could not be completed. Please try again or use a different payment method.",
  'CS-E03': "Payment could not be completed. Please try again or use a different payment method.",

  // PAYPAL ERRORS (CS-Pxx)
  'CS-P01': "PayPal is temporarily unavailable. Please try another payment method.",
  'CS-P02': "PayPal is temporarily unavailable. Please try another payment method.",
  'CS-P03': "Your PayPal payment was declined. Please try again or use a different payment method.",
  'CS-P04': "Insufficient funds in your PayPal account. Please add funds or use a different payment method.",
  'CS-P05': "Your PayPal account could not be used. Please verify your account or use a different payment method.",
  'CS-P06': "PayPal could not process this transaction. Please try a different payment method.",
  'CS-P07': "PayPal payment was not completed. Please try again.",
  'CS-P08': "PayPal payment could not be completed. Please try again.",
  'CS-P09': "Server is busy. Please try again in a moment.",
  'CS-P10': "Ticket(s) are no longer available. Please refresh and try again.",
  'CS-P11': "PayPal payment could not be completed. Please try again or use a different payment method.",
};

// ============================================
// FIELD TARGETING — Which codes show inline
// ============================================
export const FIELD_MAP = {
  'CS-SC02': 'cvv',
  'CS-FC02': 'cvv',
  'CS-SC03': 'zip',
  'CS-FC03': 'zip',
};

// ============================================
// RETRYABLE CODES
// ============================================
export const RETRYABLE_CODES = new Set([
  'CS-SC02', 'CS-SC03', 'CS-SC07',
  'CS-FC02', 'CS-FC03', 'CS-FC07', 'CS-FC13', 'CS-FC14', 'CS-FC15', 'CS-FC16', 'CS-FC18',
  'CS-SB01', 'CS-FB01',
  'CS-I01', 'CS-I02', 'CS-I03', 'CS-I04', 'CS-I05', 'CS-I06',
  'CS-E01', 'CS-E02', 'CS-E03',
  'CS-P02', 'CS-P07', 'CS-P08', 'CS-P09', 'CS-P11',
]);

// ============================================
// DONOR-CAUSED ERROR CODES
// Errors caused by the donor (card declines, bank failures, PayPal donor issues).
// These are expected business events — they should NOT be reported to Sentry.
//
// NOT included (these are system/infra errors — KEEP in Sentry):
//   CS-I01–I06 (internal system), CS-FC15–FC18 (Finix infra),
//   CS-E01–E03 (express checkout), CS-X02 (security),
//   CS-P01, CS-P02, CS-P09 (PayPal infra)
//
// Reference: docs/initiatives/completed/payment-error-codes/error-messages-final.md
// ============================================
export const DONOR_CAUSED_CODES = new Set([
  // Stripe Card declines (CS-SC01 through CS-SC09)
  'CS-SC01', 'CS-SC02', 'CS-SC03', 'CS-SC04', 'CS-SC05',
  'CS-SC06', 'CS-SC07', 'CS-SC08', 'CS-SC09',
  // Finix Card declines (CS-FC01 through CS-FC14 — NOT FC15-FC18 which are infra)
  'CS-FC01', 'CS-FC02', 'CS-FC03', 'CS-FC04', 'CS-FC05',
  'CS-FC06', 'CS-FC07', 'CS-FC08', 'CS-FC09', 'CS-FC10',
  'CS-FC11', 'CS-FC12', 'CS-FC13', 'CS-FC14',
  // Stripe Bank/ACH failures
  'CS-SB01', 'CS-SB02', 'CS-SB03',
  // Finix Bank/ACH failures
  'CS-FB01', 'CS-FB02', 'CS-FB03',
  // PayPal donor-caused errors (NOT P01/P02/P09 which are PayPal infra)
  'CS-P03', 'CS-P04', 'CS-P05', 'CS-P06', 'CS-P07', 'CS-P08', 'CS-P10', 'CS-P11',
]);

/**
 * Returns true if the error code represents a donor-caused payment failure
 * (card decline, bank issue, etc.) that should NOT be sent to Sentry.
 */
export function isDonorCausedError(code) {
  return code != null && DONOR_CAUSED_CODES.has(code);
}

// ============================================
// CATEGORY DETECTION
// ============================================
function getCategory(code) {
  if (!code) return 'system';
  if (code.startsWith('CS-SC') || code.startsWith('CS-FC')) return 'card';
  if (code.startsWith('CS-SB') || code.startsWith('CS-FB')) return 'bank';
  if (code.startsWith('CS-I')) return 'system';
  if (code.startsWith('CS-V')) return 'system';
  if (code.startsWith('CS-X')) return 'security';
  if (code.startsWith('CS-E')) return 'express_checkout';
  if (code.startsWith('CS-P')) return 'paypal';
  return 'system';
}

// ============================================
// STRIPE SDK ERROR MAPPING
// ============================================
const STRIPE_DECLINE_TO_CS = {
  'insufficient_funds': 'CS-SC06',
  'withdrawal_count_limit_exceeded': 'CS-SC06',
  'expired_card': 'CS-SC04',
  'invalid_expiry_month': 'CS-SC04',
  'invalid_expiry_year': 'CS-SC04',
  'incorrect_cvc': 'CS-SC02',
  'invalid_cvc': 'CS-SC02',
  'incorrect_zip': 'CS-SC03',
  'incorrect_address': 'CS-SC03',
  'lost_card': 'CS-SC05',
  'stolen_card': 'CS-SC05',
  'pickup_card': 'CS-SC05',
  'restricted_card': 'CS-SC05',
  'invalid_number': 'CS-SC07',
  'incorrect_number': 'CS-SC07',
  'invalid_account': 'CS-SC07',
  'generic_decline': 'CS-SC01',
  'card_declined': 'CS-SC01',
  'no_action_taken': 'CS-SC01',
  'do_not_honor': 'CS-SC08',
  'call_issuer': 'CS-SC08',
  'transaction_not_allowed': 'CS-SC08',
  'fraudulent': 'CS-SC09',
  'merchant_blacklist': 'CS-SC09',
};

// ============================================
// FINIX FORM SDK ERROR MAPPING (frontend-only)
// ============================================
const FINIX_FORM_ERROR_TO_CS = {
  'TIMEOUT': 'CS-FC15',
  'SYSTEM_ERROR': 'CS-FC16',
  'PROCESSING_ERROR': 'CS-FC16',
  'INVALID_MERCHANT': 'CS-FC16',
};

// ============================================
// FINIX FAILURE CODE MAPPING (backend errors)
// ============================================
const FINIX_FAILURE_TO_CS = {
  'GENERIC_DECLINE': 'CS-FC01',
  'DECLINED': 'CS-FC01',
  'CARD_DECLINED': 'CS-FC01',
  'INVALID_CVV': 'CS-FC02',
  'CVC_CHECK_FAILED': 'CS-FC02',
  'CVV_FAILED_RISK_RULES': 'CS-FC02',
  'INCORRECT_CVC': 'CS-FC02',
  'ADDRESS_VERIFICATION_FAILED_RISK_RULES': 'CS-FC03',
  'EXPIRED_CARD': 'CS-FC04',
  'INVALID_EXPIRATION': 'CS-FC04',
  'INVALID_EXPIRY_MONTH': 'CS-FC04',
  'INVALID_EXPIRY_YEAR': 'CS-FC04',
  'LOST_CARD': 'CS-FC05',
  'STOLEN_CARD': 'CS-FC05',
  'LOST_OR_STOLEN_CARD': 'CS-FC05',
  'PICK_UP_CARD': 'CS-FC05',
  'PICKUP_CARD': 'CS-FC05',
  'RESTRICTED_CARD': 'CS-FC05',
  'INSUFFICIENT_FUNDS': 'CS-FC06',
  'INSUFFICIENT_BALANCE': 'CS-FC06',
  'NOT_SUFFICIENT_FUNDS': 'CS-FC06',
  'INVALID_CARD_NUMBER': 'CS-FC07',
  'INVALID_NUMBER': 'CS-FC07',
  'INVALID_CARD': 'CS-FC07',
  'DO_NOT_HONOR': 'CS-FC08',
  'CALL_ISSUER': 'CS-FC08',
  'REFER_TO_CARD_ISSUER': 'CS-FC08',
  'TRANSACTION_NOT_ALLOWED': 'CS-FC08',
  'SUSPECTED_FRAUD': 'CS-FC09',
  'FRAUD_DECLINE': 'CS-FC09',
  'FRAUD_DETECTED': 'CS-FC09',
  'DUPLICATE_TRANSACTION': 'CS-FC10',
  'CARDHOLDER_PREVENTED_RECURRING_TRANSACTION': 'CS-FC11',
  'CARD_NETWORK_ERROR': 'CS-FC13',
  'COMMUNICATION_ERROR': 'CS-FC13',
  'ISSUER_INOPERATIVE': 'CS-FC14',
  'ISSUER_UNAVAILABLE': 'CS-FC14',
  'FILE_TEMPORARILY_NOT_AVAILABLE': 'CS-FC14',
};

// ============================================
// MAIN PARSER
// ============================================

function buildResult(code, overrides = {}) {
  return {
    code,
    userMessage: ERROR_MESSAGES[code] || ERROR_MESSAGES['CS-I03'],
    field: FIELD_MAP[code] || null,
    declineType: null,
    retryable: RETRYABLE_CODES.has(code),
    category: getCategory(code),
    metadata: {},
    ...overrides,
  };
}

/**
 * Build a structured error object from a CS-XXX code.
 * Useful for creating errors inline without going through the full parser.
 *
 * @param {string} code - CS-XXX error code
 * @param {string} [userMessage] - Optional override message (defaults to ERROR_MESSAGES[code])
 * @param {Object} [overrides] - Optional field overrides (field, retryable, category, etc.)
 * @returns {Object} Structured error: { code, userMessage, field, declineType, retryable, category, metadata }
 */
export function buildPaymentError(code, userMessage, overrides = {}) {
  return buildResult(code, {
    ...(userMessage ? { userMessage } : {}),
    ...overrides,
  });
}

/**
 * Parse any payment error into a structured object.
 *
 * @param {Error|Object|null} error - The error object (Stripe SDK, Finix SDK, or generic)
 * @param {Response|null} response - HTTP response object (if available)
 * @param {Object|string|null} responseBody - Parsed response body (if available)
 * @param {string} processor - 'STRIPE' | 'FINIX' | 'PAYPAL'
 * @param {Object} context - Additional context (amount, email, uuid, etc.)
 * @returns {Object} Structured error: { code, userMessage, field, declineType, retryable, category, metadata }
 */
export function parsePaymentError(error, response, responseBody, processor, context = {}) {
  // 1. Backend canonical JSON with error_code
  if (responseBody && typeof responseBody === 'object' && responseBody.error_code) {
    const code = responseBody.error_code;
    return buildResult(code, {
      userMessage: responseBody.error || ERROR_MESSAGES[code] || ERROR_MESSAGES['CS-I03'],
      field: responseBody.field || FIELD_MAP[code] || null,
      declineType: responseBody.decline_type || null,
      retryable: responseBody.retryable != null ? responseBody.retryable : RETRYABLE_CODES.has(code),
      category: responseBody.category || getCategory(code),
      metadata: {
        processor,
        processorCode: responseBody.processor_code,
        ...context,
      },
    });
  }

  // 2. Timeout errors
  if (error?.message === 'PAYMENT_TIMEOUT') {
    return buildResult('CS-I01', {
      metadata: { processor, ...context },
    });
  }

  // 3. Network/fetch errors (TypeError from failed fetch)
  if (error instanceof TypeError && error.message?.includes('fetch')) {
    return buildResult('CS-E02', {
      category: context.paymentMethod === 'google_pay' || context.paymentMethod === 'apple_pay'
        ? 'express_checkout' : 'system',
      metadata: { processor, errorMessage: error.message, ...context },
    });
  }

  // 4. Stripe SDK errors (have a `type` property like 'card_error')
  if (processor === 'STRIPE' && error?.type) {
    return parseStripeError(error, context);
  }

  // 5. Finix form SDK errors (from tokenization callback)
  if (processor === 'FINIX' && error && !responseBody) {
    return parseFinixFormError(error, context);
  }

  // 6. Finix backend errors (from API response)
  if (processor === 'FINIX' && responseBody) {
    return parseFinixBackendError(responseBody, context);
  }

  // 7. Legacy string response (rolling deploy safety)
  if (typeof responseBody === 'string') {
    return buildResult('CS-I03', {
      userMessage: responseBody || ERROR_MESSAGES['CS-I03'],
      metadata: { processor, legacyString: true, ...context },
    });
  }

  // 8. Response body is JSON but missing error/error_code fields
  if (responseBody && typeof responseBody === 'object' && !responseBody.error && !responseBody.error_code) {
    return buildResult('CS-I06', {
      metadata: { processor, ...context },
    });
  }

  // 9. Response body has error but no error_code (partial canonical)
  if (responseBody && typeof responseBody === 'object' && responseBody.error) {
    return buildResult('CS-I03', {
      userMessage: responseBody.error,
      metadata: { processor, ...context },
    });
  }

  // 10. Fallback — use error.message if available
  const fallbackMessage = error?.message || ERROR_MESSAGES['CS-I03'];
  return buildResult('CS-I03', {
    userMessage: fallbackMessage,
    metadata: { processor, ...context },
  });
}

// ============================================
// SUB-PARSERS
// ============================================

function parseStripeError(error, context) {
  const declineCode = error.decline_code || error.code;
  const csCode = STRIPE_DECLINE_TO_CS[declineCode] || 'CS-SC01';

  return buildResult(csCode, {
    userMessage: error.message || ERROR_MESSAGES[csCode],
    declineType: getStripeDeclineType(declineCode),
    metadata: {
      processor: 'STRIPE',
      stripeErrorType: error.type,
      stripeCode: error.code,
      stripeDeclineCode: error.decline_code,
      ...context,
    },
  });
}

function parseFinixFormError(error, context) {
  const errorCode = error.failure_code || error.code;

  // Check Finix failure code mapping first
  if (errorCode && FINIX_FAILURE_TO_CS[errorCode]) {
    const csCode = FINIX_FAILURE_TO_CS[errorCode];
    return buildResult(csCode, {
      declineType: getFinixDeclineType(errorCode),
      metadata: {
        processor: 'FINIX',
        finixFailureCode: errorCode,
        finixMessage: error.failure_message || error.message,
        ...context,
      },
    });
  }

  // Check Finix form SDK error mapping
  if (errorCode && FINIX_FORM_ERROR_TO_CS[errorCode]) {
    const csCode = FINIX_FORM_ERROR_TO_CS[errorCode];
    return buildResult(csCode, {
      metadata: {
        processor: 'FINIX',
        finixFormError: errorCode,
        ...context,
      },
    });
  }

  // Fallback for unknown Finix errors
  return buildResult('CS-FC16', {
    userMessage: error.failure_message || error.message || ERROR_MESSAGES['CS-FC16'],
    metadata: {
      processor: 'FINIX',
      finixFailureCode: errorCode,
      finixMessage: error.failure_message || error.message,
      ...context,
    },
  });
}

function parseFinixBackendError(responseBody, context) {
  // Check embedded errors from Finix API
  const embeddedError = responseBody._embedded?.errors?.[0];
  if (embeddedError) {
    const failureCode = embeddedError.failure_code || embeddedError.code;
    if (failureCode && FINIX_FAILURE_TO_CS[failureCode]) {
      const csCode = FINIX_FAILURE_TO_CS[failureCode];
      return buildResult(csCode, {
        declineType: getFinixDeclineType(failureCode),
        metadata: {
          processor: 'FINIX',
          finixFailureCode: failureCode,
          finixMessage: embeddedError.message,
          ...context,
        },
      });
    }
  }

  // Has error field but no error_code (old backend format)
  if (responseBody.error) {
    const errorMsg = typeof responseBody.error === 'string'
      ? responseBody.error
      : responseBody.error.message || JSON.stringify(responseBody.error);
    return buildResult('CS-FC16', {
      userMessage: errorMsg,
      metadata: { processor: 'FINIX', ...context },
    });
  }

  return buildResult('CS-FC16', {
    metadata: { processor: 'FINIX', ...context },
  });
}

// ============================================
// DECLINE TYPE HELPERS (Sentry only)
// ============================================

const SOFT_STRIPE_CODES = new Set([
  'insufficient_funds', 'issuer_unavailable', 'processing_error',
  'try_again_later', 'do_not_honor', 'generic_decline', 'card_declined',
  'transaction_not_allowed',
]);

const SOFT_FINIX_CODES = new Set([
  'INSUFFICIENT_FUNDS', 'INSUFFICIENT_BALANCE', 'ISSUER_UNAVAILABLE',
  'PROCESSING_ERROR', 'DO_NOT_HONOR', 'CARD_DECLINED', 'GENERIC_DECLINE',
  'DECLINED',
]);

function getStripeDeclineType(declineCode) {
  if (!declineCode) return 'hard';
  return SOFT_STRIPE_CODES.has(declineCode) ? 'soft' : 'hard';
}

function getFinixDeclineType(failureCode) {
  if (!failureCode) return 'hard';
  return SOFT_FINIX_CODES.has(failureCode) ? 'soft' : 'hard';
}

/**
 * Shared payment error handler.
 *
 * Parses the error, updates component state, and reports to Sentry.
 * Used by all payment error handlers (Stripe, Finix form, Finix backend,
 * express checkout) to avoid duplicating parse + setState + Sentry logic.
 *
 * Note: Sentry is passed as a parameter (not imported) to avoid making
 * @charitystack/shared depend on @sentry/react directly. Each app passes
 * its own Sentry instance.
 *
 * @param {Object} params
 * @param {Error|Object} params.error - The error from processor/backend
 * @param {string} params.processor - 'STRIPE' | 'FINIX' | 'PAYPAL'
 * @param {Function} params.setError - State setter (setServerError or setExpressCheckoutError)
 * @param {Object} [params.context={}] - Additional context (amount, email, uuid, etc.)
 * @param {string} [params.paymentMethod='card'] - For Sentry tag
 * @param {Response} [params.response=null] - HTTP response object (if available)
 * @param {Object|string} [params.responseBody=null] - Parsed response body (if available)
 * @param {Object} [params.Sentry=null] - Sentry instance for error reporting
 * @returns {Object} The parsed error object
 */
export function handlePaymentError({
  error,
  processor,
  setError,
  context = {},
  paymentMethod = 'card',
  response = null,
  responseBody = null,
  Sentry = null,
}) {
  const parsedError = parsePaymentError(error, response, responseBody, processor, context);

  setError(parsedError);

  // Only capture system/infra errors to Sentry — donor-caused errors (card declines,
  // bank failures) are expected business events and should not create Sentry noise.
  if (Sentry && !isDonorCausedError(parsedError.code)) {
    Sentry.captureException(
      error instanceof Error ? error : new Error(parsedError.userMessage),
      {
        tags: {
          payment_method: paymentMethod,
          processor,
          error_code: parsedError.code,
          error_category: parsedError.category,
          decline_type: parsedError.declineType,
          retryable: parsedError.retryable,
        },
        extra: {
          parsedError,
          ...parsedError.metadata,
          responseStatus: response?.status,
        },
      }
    );
  }

  return parsedError;
}
